import { useQuery } from '@tanstack/react-query';
import type { AgencyDashboard, MetricsResponse } from '@marketing-os/shared';
import { apiGet, apiPost } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import type { ClientDashboardResponse } from '@/types/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface PeriodParams {
  from: string;
  to: string;
}

export function useAgencyDashboard(period: PeriodParams) {
  return useQuery({
    queryKey: qk.agencyDashboard(period),
    queryFn: () => apiGet<AgencyDashboard>('/metrics/dashboard/agency', { params: period }),
  });
}

export function useClientDashboard(clientId: string | undefined, period: PeriodParams) {
  return useQuery({
    queryKey: qk.clientDashboard(clientId ?? '', period),
    queryFn: () => apiGet<ClientDashboardResponse>(`/metrics/dashboard/client/${clientId}`, { params: period }),
    enabled: Boolean(clientId),
  });
}

export interface MetricsParams extends PeriodParams {
  clientId?: string;
  level?: string;
  groupBy?: string;
  compareToPreviousPeriod?: boolean;
}

export function useMetrics(params: MetricsParams, enabled = true) {
  return useQuery({
    queryKey: qk.metrics(params),
    queryFn: () => apiGet<MetricsResponse>('/metrics', { params }),
    enabled,
  });
}

export function useSyncMetrics() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => apiPost<{ jobId: string }>(`/metrics/sync/${clientId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['metrics'] }),
  });
}
